import { AfterViewInit, Component, DestroyRef, ElementRef, inject, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { Transition } from '@uirouter/angular';

import { DisplayListOperationsService } from 'src/app/displays/services/display-list-operations.service';
import { LocalStorageHelperService } from 'src/app/shared/services/local-storage-helper.service';
import { DisplayService } from '../../services/display.service';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, Subscription } from 'rxjs';
import { LiveAnnouncerService } from 'src/app/accessibility';
import { DisplayApiService } from 'src/app/api/services/display-api.service';
import { type FolderItem } from 'src/app/api/services/folder';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { ListSearchStateService, PaginatedScrollingListService, ScrollingListStateService } from 'src/app/components/scrolling-list';
import { PageWithListComponent } from 'src/app/components/scrolling-list/page-with-list.component';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { FolderService } from 'src/app/shared/services/folder.service';

@Component({
    selector: 'display-list',
    templateUrl: './display-list.component.html',
    styleUrls: ['./display-list.component.scss'],
    standalone: false,
    providers: [
      displayListProviders()
    ]
})
export class DisplayListComponent
  extends PageWithListComponent<any>
  implements AfterViewInit, OnDestroy {

  folderId: string | null | undefined;
  folders: FolderItem[] = [];

  tableFields: { label: string; name: string; reverse: boolean; renderIf: boolean; }[];

  listOperations = this.displayListOperationsService.listOperations;

  allCompanies = false;

  selectedCompayId = this.companyStateService.getSelectedCompanyId();
  scheduleOverrides: string[] = [];

  filterConfig = {
    placeholder: 'Search Displays',
    borderless: true,
  };

  private subscription: Subscription;
  private destroyRef = inject(DestroyRef);

  @ViewChild('displaysListContainer') displaysListContainer: ElementRef;

  constructor(
    private broadcaster: BroadcasterService,
    public userStateService: UserStateService,
    private companyStateService: CompanyStateService,
    public displayService: DisplayService,
    private displayListOperationsService: DisplayListOperationsService,
    protected folderService: FolderService,
    private transition: Transition,
    private localStorageHelperService: LocalStorageHelperService,
    private liveAnnouncerService: LiveAnnouncerService,
    private ngZone: NgZone
  )
  {
    super();

    this.subscription = this.broadcaster.subscribe({
      next: (event: String) => {
        switch (event) {
        case 'risevision.company.planStarted':
          // use doSearch because it clears the list
          this.searchState.reset();
          break;
        default:
          return;
        }
      }
    });

    const savedAllCompanies = this.localStorageHelperService.get('displayList.allCompanies');
    if (savedAllCompanies !== undefined && savedAllCompanies !== null) {
      this.allCompanies = savedAllCompanies;
      this.searchState.setIncludeSubcompanies(savedAllCompanies);
    }
    this.folderId = this.transition.params().folderId;
    if (this.folderId) {
      this.searchState.setFolderId(this.folderId);
    }

    this.tableFields = [
      { label: 'Name', name: 'name', reverse: false, renderIf: true },
      { label: 'Company', name: 'companyName', reverse: false, renderIf: this.allCompanies },
      { label: 'Licensed', name: 'licensed', reverse: false, renderIf: true },
      { label: 'Status', name: 'onlineStatus', reverse: false, renderIf: true },
      { label: 'Last Connection', name: 'lastActivityDate', reverse: false, renderIf: true },
      { label: 'Schedule', name: 'scheduleName', reverse: false, renderIf: true },
      { label: 'Last Modified', name: 'changeDate', reverse: false, renderIf: true },
    ];

    this.updateMoveAfterAction();
    this.listService.setOperations(this.listOperations);
  }

  ngAfterViewInit(): void {
    this.searchState.init({
      ...this.searchState.getSearchObject(),
      sortBy: 'name',
      reverse: false,
    });
    this.listService.load();
    this.onScroll.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(500)
    ).subscribe(() => {
      this.refresh();
    });
    
    const { sortBy, reverse } = this.searchState.getSearchObject();
    const field = this.tableFields.find(f => f.name === sortBy);
    if (field) {
      this.announceSort(field.label, reverse ? 'descending' : 'ascending');
    }

    this.initDraggableService('.displays-list-container, .folder-path', '.displays-list-container');
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  toggleAllCompanies(value) {
    this.allCompanies = value;
    this.localStorageHelperService.set('displayList.allCompanies', this.allCompanies);

    this.searchState.setIncludeSubcompanies(value);
    this.searchState.doSearch();

    const companyNameField = this.tableFields.find(field => field.name === 'companyName');
    if (companyNameField) {
      companyNameField.renderIf = value;
    }
  }

  getLicenseStatus(display) {
    if (display && display.playerProAuthorized) {
      return 'Yes';
    } else if (display && !display.playerProAuthorized && display.isFreeForURLs) {
      return 'Free';
    } else {
      return '−';
    }
  }

  refresh () {
    setTimeout(() => {
      this.ngZone.run(() => {});
    });
  }

  showPopover (overrideNames: string[]) {
    this.scheduleOverrides = overrideNames;
    this.refresh();
  }

  getDestroyRef(): DestroyRef {
    return this.destroyRef;
  }

  onHeaderResize(event: { width: number, height: number }) {
    if (this.displaysListContainer) {
      this.displaysListContainer.nativeElement.style.setProperty('--displays-list-header-height', event.height + 'px');

      this.checkViewportSize();
    }
  }

  onFolderClicked(folderId: string) {
    this.transition.router.stateService.go('apps.displays.list', { folderId });
  }

  sort(field: {name: string, label: string, reverse: boolean}) {
    // Get current sort state before changing it
    const currentSort = this.searchState.getSearchObject();
    const wasAlreadySorted = currentSort.sortBy === field.name;
    const willBeReversed = wasAlreadySorted ? !currentSort.reverse : field.reverse;
    
    // Perform the sort
    this.searchState.sortBy(field.name, willBeReversed);
    
    // Announce the sort change
    this.announceSort(field.label, willBeReversed ? 'descending' : 'ascending');
  }

  announceSort(fieldLabel: string, direction: 'ascending' | 'descending') {
    this.liveAnnouncerService.announce(`Table sorted by ${fieldLabel}, ${direction} order`);
  }

  protected doSearch = () => {
    this.searchState.doSearch();
  };

  onFolderAdded() {
    this.listService.reload();
  }

  get tableColumns() {
    return this.tableFields.filter(field => field.renderIf);
  }
}

function displayListProviders() {
  return [
    { provide: 'WithListService', useExisting: DisplayApiService },
    { provide: 'ListKind', useValue: 'Display' },
    { provide: ListSearchStateService },
    { provide: ScrollingListStateService },
    { provide: PaginatedScrollingListService },
  ];
}
